/*
 * Settings, Website: which brand websites may send requests here, the ZIP codes this location serves, and the latest
 * newsletter and popup signups. The price list itself never goes to the website; it asks the server for each price.
 */
import { useState } from "react";
import { useSettings, useSignups, useTerritories, useSaveTerritories, useUpdateSettings } from "../../api/hooks";
import { useSession } from "../../app/session";
import { useToast } from "../../components/overlays";
import { Button, Card, Details, Skeleton } from "../../components/parts";
import { shortDate } from "../../components/format";
import { errText } from "../../components/work";

const CHANNEL_WORDS: Record<string, string> = {
  google_ads: "Google Ads",
  google: "Google search",
  google_business: "Google profile",
  facebook_ads: "Facebook ads",
  facebook: "Facebook",
  bing_ads: "Bing ads",
  email: "Email",
  referral: "Another website",
  direct: "Typed in or saved",
};
export const channelWords = (c: string | null) => (c ? (CHANNEL_WORDS[c] ?? c.replace(/_/g, " ")) : "Not known");

export function WebsiteSettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const zips = useTerritories();
  const signups = useSignups(can("lead.read"));
  const saveSettings = useUpdateSettings();
  const saveZips = useSaveTerritories();
  const toast = useToast();
  const owner = can("settings.update");
  const [sites, setSites] = useState<string | null>(null);
  const [zipText, setZipText] = useState<string | null>(null);
  if (!s.data || !zips.data) return <Skeleton rows={6} />;
  const origins = s.data.settings.website.origins;

  return (
    <>
      <Card
        id="websites"
        title="Websites that send requests"
        action={
          owner && sites === null ? (
            <Button type="secondary" size="small" icon="edit" onClick={() => setSites(origins.join("\n"))}>
              Edit
            </Button>
          ) : null
        }
      >
        {sites === null ? (
          <Details rows={[["Allowed", origins.length ? origins.join(", ") : "None yet. The website forms stay closed."]]} />
        ) : (
          <div className="stack">
            <label className="field wide">
              <span className="label">One website per line, like https://thecleanersusa.com</span>
              <textarea rows={3} value={sites} onChange={(e) => setSites(e.target.value)} />
            </label>
            <div className="row end">
              <Button type="tertiary" onClick={() => setSites(null)}>
                Cancel
              </Button>
              <Button
                loading={saveSettings.isPending}
                onClick={() =>
                  saveSettings.mutate(
                    {
                      website: {
                        origins: sites
                          .split(/\s+/)
                          .map((x) => x.trim().replace(/\/$/, ""))
                          .filter(Boolean),
                      },
                    },
                    {
                      onSuccess: () => {
                        setSites(null);
                        toast("Saved");
                      },
                      onError: (e) => toast(errText(e), { kind: "error" }),
                    },
                  )
                }
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card
        id="zips"
        title="ZIP codes we serve"
        action={
          owner && zipText === null ? (
            <Button type="secondary" size="small" icon="edit" onClick={() => setZipText(zips.data.zips.join(" "))}>
              Edit
            </Button>
          ) : null
        }
      >
        {zipText === null ? (
          <Details
            rows={[
              [
                `${String(zips.data.zips.length)} ZIP codes`,
                zips.data.zips.length ? zips.data.zips.join(", ") : "None yet. Requests from any ZIP come here.",
              ],
            ]}
          />
        ) : (
          <div className="stack">
            <label className="field wide">
              <span className="label">Type or paste the ZIP codes, with spaces or commas between</span>
              <textarea rows={4} value={zipText} onChange={(e) => setZipText(e.target.value)} />
            </label>
            <div className="row end">
              <Button type="tertiary" onClick={() => setZipText(null)}>
                Cancel
              </Button>
              <Button
                loading={saveZips.isPending}
                onClick={() =>
                  saveZips.mutate(zipText.match(/\b\d{5}\b/g) ?? [], {
                    onSuccess: () => {
                      setZipText(null);
                      toast("Saved");
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  })
                }
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </Card>

      {signups.data ? (
        <Card id="signups" title="Newsletter and popup signups" flush>
          {signups.data.signups.length === 0 ? (
            <p className="muted auto-intro">No signups yet.</p>
          ) : (
            <ul className="auto-list">
              {signups.data.signups.slice(0, 50).map((x) => (
                <li key={x.id} className="setting-row">
                  <span className="what">
                    <b>{x.name ?? x.email ?? x.phone}</b>
                    <span>
                      {[x.email, x.phone, x.zip].filter(Boolean).join(", ")}. From {channelWords(x.channel)}.
                    </span>
                  </span>
                  <span className="muted">{shortDate(x.createdAt.slice(0, 10))}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}
    </>
  );
}
